import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SubjectIcon from '@mui/icons-material/Subject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { resources } from '../../core/services/resources';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { Pill } from './Pill';
import { SEMANTIC_COLOR_VARS, hashSemanticColor } from './semanticColor';
import { DocumentStatusEnum } from '../../core/types/data-models';
import type {
  Document as ApiDocument,
  DocumentCategory,
  DocumentSection,
  DocumentType,
} from '../../core/types/data-models';

/** Small status indicator mirroring a document's review state — approved
 * (check), in review (pulsing dot), draft (clock). Archived shows nothing. */
function DocumentStatusMark({ status }: { status: DocumentStatusEnum }) {
  if (status === DocumentStatusEnum.APPROVED) {
    return <CheckCircleIcon sx={{ fontSize: 12, color: 'var(--color-success)' }} />;
  }
  if (status === DocumentStatusEnum.IN_REVIEW) {
    return (
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'var(--color-warning)',
          flexShrink: 0,
        }}
      />
    );
  }
  if (status === DocumentStatusEnum.DRAFT) {
    return <ScheduleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />;
  }
  return null;
}

// ── Sections sub-tree (lazy-loaded when doc is expanded) ────────────────────

function SectionsTree({ documentId }: { documentId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['document-sections', documentId],
    queryFn: () =>
      resources.documentSections.list({
        filter: { documentId: [documentId] },
        sort: 'order',
        pageSize: 100,
      }),
  });

  if (isLoading) {
    return (
      <Box sx={{ pl: 8, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={10} />
        <Typography variant="caption" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  const sections: DocumentSection[] = (data?.data ?? []).filter(
    (s) => !s.parentDocumentSectionId,
  );

  return (
    <>
      {sections.map((s) => (
        <ListItemButton
          key={s.id}
          sx={{ pl: 8, py: 0.3, borderRadius: 1 }}
          disabled
        >
          <ListItemIcon sx={{ minWidth: 20 }}>
            <SubjectIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          </ListItemIcon>
          <ListItemText
            primary={s.title}
            primaryTypographyProps={{ variant: 'caption', fontSize: 11 }}
          />
        </ListItemButton>
      ))}
    </>
  );
}

/** Whether `doc` or any of its (nested) sub-documents is the current route. */
function isDocPathActive(doc: ApiDocument, docsMap: Map<number, ApiDocument[]>, pathname: string): boolean {
  if (pathname === `/documents/${doc.id}`) return true;
  return (docsMap.get(doc.id) ?? []).some((sd) => isDocPathActive(sd, docsMap, pathname));
}

// ── Single document row (recursive for sub-documents) ───────────────────────

function DocumentItem({
  doc,
  docsMap,
  depth = 0,
}: {
  doc: ApiDocument;
  docsMap: Map<number, ApiDocument[]>;
  depth?: number;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isActive = pathname === `/documents/${doc.id}`;
  const subDocs = docsMap.get(doc.id) ?? [];

  // Path-to-selection: if the active document is nested under this one, its
  // ancestor chain should be revealed regardless of what was last persisted.
  const containsActiveDescendant = useMemo(
    () => subDocs.some((sd) => isDocPathActive(sd, docsMap, pathname)),
    [subDocs, docsMap, pathname],
  );
  const [open, setOpen] = useLocalStorageState<boolean>(
    `sidebar.contentTree.document.${doc.id}`,
    () => containsActiveDescendant,
  );
  useEffect(() => {
    if (containsActiveDescendant) setOpen(true);
    // Only ever force-*open* on navigation — a manual collapse afterwards must stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsActiveDescendant]);

  return (
    <>
      <ListItemButton
        selected={isActive}
        onClick={() => navigate(`/documents/${doc.id}`)}
        sx={{ pl: 4.5 + depth * 1.5, py: 0.3, borderRadius: 1, mx: 1 }}
      >
        {/* Chevron — stops propagation so it only toggles, doesn't navigate */}
        <ListItemIcon
          sx={{ minWidth: 18, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        >
          {open ? (
            <ExpandMoreIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          )}
        </ListItemIcon>

        <ListItemIcon sx={{ minWidth: 22 }}>
          <ArticleOutlinedIcon
            sx={{ fontSize: 13, color: isActive ? 'primary.main' : 'text.secondary' }}
          />
        </ListItemIcon>

        <ListItemText
          primary={doc.name}
          primaryTypographyProps={{
            variant: 'body2',
            fontSize: 12,
            fontWeight: isActive ? 600 : 400,
            noWrap: true,
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 0.5 }}>
          <DocumentStatusMark status={doc.status} />
        </Box>
      </ListItemButton>

      <Collapse in={open} unmountOnExit>
        {/* Sub-documents first */}
        {subDocs.map((sd) => (
          <DocumentItem key={sd.id} doc={sd} docsMap={docsMap} depth={depth + 1} />
        ))}
        {/* Then root sections of this document */}
        <SectionsTree documentId={doc.id} />
      </Collapse>
    </>
  );
}

// ── Category group ───────────────────────────────────────────────────────────

function CategoryItem({
  category,
  docs,
  docsMap,
}: {
  category: DocumentCategory;
  docs: ApiDocument[];
  docsMap: Map<number, ApiDocument[]>;
}) {
  const { pathname } = useLocation();
  const color = hashSemanticColor(category.type);
  const vars = SEMANTIC_COLOR_VARS[color];

  // Expanded by default, so a project's documents are visible without a click.
  // A manual collapse is remembered per category (localStorage), and the path
  // to the active document is force-opened below regardless.
  const containsActiveDoc = useMemo(
    () => docs.some((d) => isDocPathActive(d, docsMap, pathname)),
    [docs, docsMap, pathname],
  );
  const [open, setOpen] = useLocalStorageState<boolean>(
    `sidebar.contentTree.category.${category.type}`,
    true,
  );
  useEffect(() => {
    if (containsActiveDoc) setOpen(true);
    // Only ever force-*open* on navigation — a manual collapse afterwards must stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsActiveDoc]);

  if (!docs.length) return null;

  return (
    <Box sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={() => setOpen((o) => !o)}
        sx={{ py: 0.4, px: 2, borderRadius: 1 }}
      >
        <ListItemIcon sx={{ minWidth: 18 }}>
          {open ? (
            <ExpandMoreIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          )}
        </ListItemIcon>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: vars.main, mr: 1, flexShrink: 0 }} />
        <Typography
          variant="overline"
          sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1, letterSpacing: '0.06em' }}
        >
          {category.name}
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Pill color={color}>{docs.length}</Pill>
        </Box>
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        <List dense disablePadding>
          {docs.map((doc) => (
            <DocumentItem key={doc.id} doc={doc} docsMap={docsMap} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

// ── Root tree ────────────────────────────────────────────────────────────────

export function ProjectContentTree() {
  const { activeProject } = useActiveProject();

  const categoriesQuery = useQuery({
    queryKey: ['document-categories'],
    queryFn: () => resources.documentCategories.list({ sort: 'order', pageSize: 100 }),
  });

  const documentsQuery = useQuery({
    queryKey: ['sidebar-documents', activeProject?.id],
    queryFn: () =>
      resources.documents.list({
        filter: { projectId: [activeProject!.id] },
        pageSize: 200,
      }),
    enabled: !!activeProject,
  });

  const docTypesQuery = useQuery({
    queryKey: ['document-types-all'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });

  if (!activeProject) {
    return (
      <Box sx={{ px: 2.5, py: 0.5 }}>
        <Typography variant="caption" color="text.disabled">
          Select a project to see content
        </Typography>
      </Box>
    );
  }

  if (categoriesQuery.isLoading || documentsQuery.isLoading || docTypesQuery.isLoading) {
    return (
      <Box sx={{ px: 2.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  const categories = categoriesQuery.data?.data ?? [];
  // IN_GENERATION documents are still being written and aren't ready to show yet.
  const documents = (documentsQuery.data?.data ?? []).filter((d) => d.status !== 'IN_GENERATION');

  const docTypes: DocumentType[] = docTypesQuery.data?.data ?? [];

  // Map documentType string → categoryType string for grouping
  const docTypeToCategoryType = new Map<string, string>(
    docTypes.map((dt) => [dt.type, dt.categoryType]),
  );

  // Map documentType string → DocumentType.order for sorting; types without an
  // order (or unknown types) sort last, keeping their relative order.
  const docTypeToOrder = new Map<string, number>(
    docTypes
      .filter((dt) => dt.order != null)
      .map((dt) => [dt.type, dt.order as number]),
  );
  const sortByDocTypeOrder = (docs: ApiDocument[]) =>
    [...docs].sort(
      (a, b) =>
        (docTypeToOrder.get(a.documentType) ?? Number.MAX_SAFE_INTEGER) -
        (docTypeToOrder.get(b.documentType) ?? Number.MAX_SAFE_INTEGER),
    );

  // Build parentId → children map for sub-document hierarchy
  const docsMap = new Map<number, ApiDocument[]>();
  documents.forEach((d) => {
    if (d.parentDocumentId != null) {
      const arr = docsMap.get(d.parentDocumentId) ?? [];
      arr.push(d);
      docsMap.set(d.parentDocumentId, arr);
    }
  });
  docsMap.forEach((children, parentId) => {
    docsMap.set(parentId, sortByDocTypeOrder(children));
  });

  if (!documents.length) {
    return (
      <Box sx={{ px: 2.5, py: 0.5 }}>
        <Typography variant="caption" color="text.disabled">
          No documents yet
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {categories.map((cat) => {
        // Only root documents (no parent) for this category
        const catDocs = sortByDocTypeOrder(
          documents.filter(
            (d) =>
              docTypeToCategoryType.get(d.documentType) === cat.type &&
              d.parentDocumentId == null,
          ),
        );
        return <CategoryItem key={cat.id} category={cat} docs={catDocs} docsMap={docsMap} />;
      })}
    </List>
  );
}
